<template>
    <div>
        <component-loader v-if="isLoading" />
        <div class="plant-page-wrapper" v-else>
            <div v-if="plantInfo" class="plant-details">
                <div class="plant-name">
                    <h1>{{ plantDisplayValue }}</h1>
                    <div class="flex-row-with-gap">
                        <icofont-icon v-if="plantInfo.fuelIcon" size="extra-large" :icon="plantInfo.fuelIcon" background-colour="var(--sparkOrange)" />
                        <div class="flag">
                            <country-flag :iso-code="countryCode.toLowerCase()" />
                        </div>
                    </div>
                </div>
                <div class="plant-types">
                    <metric-with-value v-if="plantInfo.fuel" metric="Fuel" :value="plantInfo.fuel" />
                    <metric-with-value v-if="plantInfo.capacity" metric="Generation Capacity" :value="plantInfo.capacity" />
                    <metric-with-value metric="Country" :value="countryName" />
                    <metric-with-value v-if="plantStartDate" metric="Implementation Date" :value="plantStartDate" />
                    <metric-with-value v-if="plantInfo.parentName" metric="Parent Plant" :value="plantInfo.parentName" />
                </div>
            </div>
            <div class="chart-wrapper" v-if="configs">
                <div
                    class="chart"
                    v-for="config in configs"
                    :key="config.info.id"
                >
                    <module-chart-with-options
                        :chart-config="config"
                        :title="config.info.name ?? ''"
                        :blocked-actions="NON_USER_DASHBOARD_BLOCKED_ACTIONS"
                        :date-override="props.dateOverride"
                        is-modal
                        display-options-top
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { IModuleChartConfig } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { IPlantInfo } from '@/interfaces/powerMarkets/plant/IPlantInfo.ts';
import { plantApi } from '@/api/powerMarkets/plantApi.ts';
import { chartApi } from '@/api/powerMarkets/chartApi.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import ModuleChartWithOptions from '@/components/powerMarkets/chart/ModuleChartWithOptions.vue';
import { NON_USER_DASHBOARD_BLOCKED_ACTIONS } from '@/interfaces/actions/enact/IModuleActions.ts';
import dayjs from 'dayjs';
import MetricWithValue from '@/components/common/MetricWithValue.vue';
import CountryFlag from '@/components/powerMarkets/shared/CountryFlag.vue';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import { ICountry } from '@/interfaces/general/ICountry.ts';
import { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import IcofontIcon from '../common/IcofontIcon.vue';
import { getPlantChartDate } from './plantChartDate.ts';

const dataStore = useDataStore();

const props = defineProps<{
    plantId: string;
    fromDate?: Date;
    toDate?: Date;
    country: string;
    dateOverride?: IDashboardOverrides;
}>();

const plantDisplayValue = computed<string>(() => dataStore.getPlantNameById(props.plantId));

const configs = ref<Array<IModuleChartConfig>>([]);
const plantInfo = ref<IPlantInfo | null>(null);
const chartsToGet = ['generation', 'remit'];
const isLoading = ref<boolean>(false);

const plantStartDate = computed<string | null>(() => {
    if (!plantInfo.value?.implementationDate) return null;
    return dayjs(plantInfo.value.implementationDate).format('DD/MM/YYYY');
});

const country = computed<ICountry | null>(() => dataStore.staticStore?.countries.find((item) => item.id === props.country || item.mainSubCountryId === props.country) ?? null);

const countryName = computed<string>(() => country.value?.name ?? '');

const countryCode = computed<string>(() => country.value?.shortName ?? '');

async function getPageData (): Promise<void> {
    await plantApi.getEntsoePlantInfo(props.plantId, props.country).then((response) => {
        if (response.data) plantInfo.value = response.data;
    });

    const chartPayload = chartsToGet.map((chart) => ({ plantId: props.plantId, chartType: chart, country: props.country }));

    await chartApi.getPlantChartSeriesConfig(chartPayload).then((response) => {
        const chartConfigs = response;

        configs.value = chartConfigs.filter((config) => !!config).map((config) => ({
            ...config,
            info: {
                ...config.info,
                name: config.info.name ? config.info.name.split(' - ')[0] : '',
                date: getPlantChartDate(config, props.fromDate, props.toDate),
            },
        }));
    });
}

async function initialise (): Promise<void> {
    isLoading.value = true;
    await getPageData();
    isLoading.value = false;
}

initialise();
</script>

<style lang="scss" scoped>
@use '/src/assets/scss/components/powerMarkets/europePlants.scss';
</style>
